import { ConsoleLogger } from '@proposify/frontend-observability';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'components/editor/grid/reduxStore/Store';
import { useAppDispatch } from 'components/editor/grid/reduxStore/Store';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import IcoLock from 'components/icon/icons/IcoLock';
import IcoLockOpen from 'components/icon/icons/IcoLockOpen';
import IcoHelp from 'components/icon/icons/IcoHelp';

import { EditorDefaultSidePanelType, setActiveSidePanel } from 'components/editor/grid/reduxStore/blockStyleSettingsSlice';
import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';

import {
  SecondaryMenu,
  SecondaryMenuSectionTitle,
  TextInputBoxSingleCol,
  NumberInputBoxSingleCol,
} from 'components/editor/SidePanel/component';

import { useSelectionContext } from 'components/editor/GridDndEditor/SelectedBlockInfoProvider';
import { useBlockContentWithSectionId } from 'components/editor/Sections/useBlockContent';
import { useVideoWithUndoRedoBlockSettings } from 'components/editor/hooks/useVideoWithUndoRedoBlockSettings';
import type { VideoContent } from 'components/editor/videos/video';
import { DIMENSION_WIDTH, DIMENSION_HEIGHT } from 'components/editor/SidePanel/content/DesignSettings/common/useAspectRatio';
import { InvalidSource, InvalidUrl, InvalidVideo } from 'components/editor/videos/videoErrors';
import { useNewAspectRatio } from 'components/editor/SidePanel/content/DesignSettings/common/useNewAspectRatio.ts';

export function VideoWithUndoRedoBlockSettings() {
  const { selectedBlockIdByWrapper } = useSelectionContext();
  const blockId = selectedBlockIdByWrapper as string;
  const sectionId = useSelector((state: RootState) => selectSectionIdByBlockId(state, blockId));
  const blockContent = useBlockContentWithSectionId(sectionId ?? '', blockId);

  if (!blockContent) {
    return null;
  }

  return <VideoBlockSettingsContent key={blockId} blockContent={blockContent} blockId={blockId} sectionId={sectionId as string} />;
}

const VideoBlockSettingsContent = ({
  blockContent,
  blockId,
  sectionId,
}: {
  blockContent: BlockContent;
  blockId: string;
  sectionId: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { updateVideoContent, getVideoDataFromUrl, clearVideoContent } = useVideoWithUndoRedoBlockSettings();
  const videoContent = { url: '', aspectRatioLocked: true, ...blockContent?.contentObject } as VideoContent;

  const { handleImageDimensionChange } = useNewAspectRatio({
    imageAspectRatioLock: videoContent.aspectRatioLocked,
    sectionId: sectionId ?? '',
    blockId,
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [videoUrl, setVideoUrl] = useState(videoContent?.url);
  const [urlInputError, setUrlInputError] = useState('');

  useEffect(() => {
    setVideoUrl(videoContent.url);
  }, [videoContent.url]);

  const width = blockContent?.blockConfig.width || 0;
  const height = blockContent?.blockConfig.height || 0;

  const [typedWidth, setTypedWidth] = useState<number | null>(null);
  const [typedHeight, setTypedHeight] = useState<number | null>(null);

  const originalDimensionsRef = useRef<{
    width: number;
    height: number;
    blockId: string;
    blockContent: BlockContent;
  } | null>(null);

  useEffect(() => {
    setTypedWidth(null);
    setTypedHeight(null);
    originalDimensionsRef.current = null;
  }, [blockId]);

  const handleDimensionFocus = useCallback(() => {
    originalDimensionsRef.current = {
      width,
      height,
      blockId,
      blockContent: {
        ...blockContent,
        blockConfig: { ...blockContent.blockConfig },
      },
    };
  }, [width, height, blockId, blockContent]);

  const handleWidthChange = (newWidth: number) => {
    setTypedWidth(newWidth);

    if (originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_WIDTH, newWidth, false, originalDimensionsRef.current);
    }
  };

  const handleWidthBlur = () => {
    if (typedWidth !== null && originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_WIDTH, typedWidth, true, originalDimensionsRef.current);
    }

    setTypedWidth(null);
  };

  const handleHeightChange = (newHeight: number) => {
    setTypedHeight(newHeight);

    if (originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_HEIGHT, newHeight, false, originalDimensionsRef.current);
    }
  };

  const handleHeightBlur = () => {
    if (typedHeight !== null && originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_HEIGHT, typedHeight, true, originalDimensionsRef.current);
    }

    setTypedHeight(null);
  };

  const handleUrlOnPaste = async () => {
    setTimeout(() => {
      inputRef.current?.blur();
    }, 0);
  };

  const handleVideoUrlChange = async () => {
    if (videoUrl === videoContent.url) {
      return;
    }

    try {
      const videoData = await getVideoDataFromUrl(videoUrl);
      await updateVideoContent({
        ...videoContent,
        ...videoData,
        url: videoUrl,
      });
      setUrlInputError('');
    } catch (error) {
      await clearVideoContent();

      if (error instanceof InvalidUrl) {
        setUrlInputError(t('document.grid.video.design.errors.invalid_url'));
        return;
      }

      if (error instanceof InvalidSource) {
        setUrlInputError(t('document.grid.video.design.errors.invalid_source'));
        return;
      }

      if (error instanceof InvalidVideo) {
        setUrlInputError(t('document.grid.video.design.errors.invalid_video'));
        return;
      }

      setUrlInputError(t('document.grid.video.design.errors.unknown'));
      ConsoleLogger.error('VideoBlockSettings - Error updating video content from URL', error);
    }
  };

  const handleBackButtonClick = () => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  };

  return (
    <SecondaryMenu
      testId="video-block-settings-component"
      menuTitle={t('document.grid.video.design.title')}
      backButtonOnClick={handleBackButtonClick}
    >
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.video.design.general')}</SecondaryMenuSectionTitle>

        <Stack gap={1} direction="row" alignItems="end">
          <TextInputBoxSingleCol
            title={
              <Stack gap={1} direction="row">
                {t('document.grid.video.design.url')}
                <IcoHelp id="video-help" width={20} height={20} />
              </Stack>
            }
            inputRef={inputRef}
            placeholderText={t('document.grid.video.design.url_placeholder')}
            onChange={(value) => setVideoUrl(value)}
            onBlur={handleVideoUrlChange}
            onPaste={handleUrlOnPaste}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                inputRef.current?.blur();
              }
            }}
            value={videoUrl}
            testId="video-url-input"
            error={urlInputError}
          />
        </Stack>

        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.video.design.display')}</SecondaryMenuSectionTitle>

        <Stack gap={1} direction="row" alignItems="end">
          <Stack direction="row" gap={2}>
            <NumberInputBoxSingleCol
              title={t('document.grid.video.design.width')}
              onChange={handleWidthChange}
              onBlur={handleWidthBlur}
              onFocus={handleDimensionFocus}
              value={width}
              endAdornmentText="px"
              testId="image-width-input"
            />
            <NumberInputBoxSingleCol
              title={t('document.grid.video.design.height')}
              onChange={handleHeightChange}
              onBlur={handleHeightBlur}
              onFocus={handleDimensionFocus}
              value={height}
              endAdornmentText="px"
              testId="image-height-input"
            />
          </Stack>

          <IconButton
            data-testid="aspect-ratio-lock-button"
            onClick={() => updateVideoContent({ ...videoContent, aspectRatioLocked: !videoContent.aspectRatioLocked })}
          >
            {videoContent.aspectRatioLocked ? <IcoLock /> : <IcoLockOpen />}
          </IconButton>
        </Stack>
      </Stack>
    </SecondaryMenu>
  );
};
